/**
 * COVER & PREVIEW — the block at the top of the wizard's REVIEW step (event-page handoff §3).
 *
 * The host picks how many photos go in the cover, a layout, drags each photo to set its
 * framing, checks the real event page on desktop and on a phone, picks the grid-card photo
 * and sees the Events-page card. Photos are the poster + the step-2 gallery images in order;
 * reordering here reorders them in step 2 too (`onMediaChanged` bumps the manager's refresh).
 *
 * State lives here and is saved through `PUT /events/{id}/cover` (debounced), so the page
 * draws what the host last saw. `form` carries the wizard's current values so the previews
 * always show the title, date, place, copy, categories and price the host is typing.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { eventService } from '../../services';
import useImageRatios from '../../hooks/useImageRatios';
import EventApplyPage from '../../pages/EventApplyPage';
import WallEventCard from '../wall/WallEventCard';
import { cardGeometry } from '../wall/wallCardGeometry';
import {
  allCoverPhotos, autoLayout, validLayouts, resolveLayout, coverGeom, normalizeSplit, layoutNote, isPortrait, dragFocal, focalPosition,
  LAYOUT_META, SPLITS, POSTER_ID,
} from '../event/coverLayout';
import { Card, ON } from './kit';

const DESKTOP_W = 1280;
const DESKTOP_H = 860;
const MOBILE_W = 390;
const MOBILE_H = 780;
const M_ZOOM = 0.8;
const SAVE_MS = 700;
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

const label = { fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase' };
const small = { fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase' };
const help = { fontSize: 11, lineHeight: 1.6, color: '#6B7280' };
const pillBtn = (on, extra = {}) => ({
  height: 38, padding: '0 16px', border: '2px solid transparent', borderRadius: 999, fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', cursor: 'pointer', textTransform: 'uppercase',
  background: on ? '#000' : '#F2F2F2', color: on ? '#fff' : '#000', ...extra,
});

/** Which page variant the wizard's form describes (handoff §5 `reviewVariant`). */
export function reviewVariant(form) {
  if (form?.isExternal) return 'external';
  if (form?.isFree) return form?.mode === 'curated' ? 'curated' : 'free';
  if (form?.mode === 'curated') return 'curated';
  if (form?.fundingType === 'party_starter') return 'starter';
  if (form?.pricingMode === 'pay_what_you_want') return 'pwyw';
  return 'guaranteed';
}

const cents = (dollars) => { const n = Math.round(parseFloat(dollars) * 100); return Number.isFinite(n) ? n : 0; };

/** An EventDto the public page can draw from the wizard's form + what the server holds. */
export function previewEventFrom(event, form, extra = {}) {
  const variant = reviewVariant(form);
  const starter = !form?.isExternal && !form?.isFree && form?.fundingType === 'party_starter';
  const price = form?.isFree || form?.isExternal ? 0 : cents(form?.ticketPrice);
  const dateTime = form?.dateTime ? new Date(form.dateTime).toISOString() : (event?.dateTime || new Date(Date.now() + 7 * 864e5).toISOString());
  const endDateTime = form?.endDateTime ? new Date(form.endDateTime).toISOString() : null;
  const deadline = form?.fundingDeadline ? new Date(form.fundingDeadline).toISOString() : dateTime;
  return {
    id: event?.id || 'preview',
    inviteCode: event?.inviteCode || 'preview',
    host: event?.host || null,
    coHosts: event?.coHosts || [],
    media: event?.media || [],
    posterUrl: event?.posterUrl || null,
    houseRules: event?.houseRules || [],
    links: event?.links || [],
    ...event,
    ...extra,
    title: (form?.title || '').trim() || 'Untitled event',
    description: form?.description || '',
    dateTime,
    endDateTime,
    fundingDeadline: deadline,
    locationAddress: form?.location || '',
    locationCity: form?.locationCity || '',
    venueName: form?.venueName || '',
    venueAddress: form?.venueAddress || '',
    locationRevealed: false,
    categories: Array.isArray(form?.categories) ? form.categories : [],
    customCategory: form?.customCategory || '',
    mode: form?.mode === 'curated' ? 'curated' : 'open',
    fundingType: starter ? 'party_starter' : 'guaranteed',
    status: starter ? 'funding' : 'selling',
    goalType: 'cash',
    pricingMode: variant === 'pwyw' ? 'pay_what_you_want' : 'fixed',
    ticketPriceCents: price,
    displayPriceCents: price,
    hasMultiplePrices: false,
    tiers: [],
    fundingGoalCents: starter ? cents(form?.fundingGoal) : 0,
    totalPledgedCents: 0,
    acceptedCount: 0,
    applicationCount: 0,
    maxAttendees: parseInt(form?.maxAttendees, 10) > 0 ? parseInt(form.maxAttendees, 10) : null,
    isExternalTicketing: !!form?.isExternal,
    externalTicketUrl: form?.isExternal ? form?.externalTicketUrl || '' : '',
    externalTicketDisplayPrice: form?.isExternal ? form?.externalTicketDisplayPrice || '' : '',
    isHostWanted: false,
    viewerCanEdit: false,
    hostId: null,
    isBookmarked: false,
    giveTakeEnabled: form?.giveTakeEnabled !== false,
  };
}

/** A layout picker card: the host's real photos in the layout's geometry + a fake title block. */
function LayoutThumb({ layoutKey, photos, split, on, suggested, onClick }) {
  const g = coverGeom(layoutKey, photos, 1216, 520, false, split);
  const meta = LAYOUT_META[layoutKey];
  return (
    <button type="button" onClick={onClick} aria-pressed={on} data-testid={`cover-layout-${layoutKey}`}
      style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 10, border: '2px solid transparent', borderRadius: 20, background: on ? ON : '#F5F5F5', color: on ? '#fff' : '#000', fontFamily: 'inherit', textAlign: 'left', cursor: 'pointer', transition: 'background-color .2s, color .2s' }}>
      <span style={{ position: 'relative', display: 'block', width: '100%', aspectRatio: '2.35', borderRadius: 10, overflow: 'hidden', background: '#fff' }}>
        {g.tiles.map((t) => {
          const p = photos[t.i] || photos[0];
          return (
            <span key={`${t.i}-${t.x}-${t.y}`} style={{ position: 'absolute', left: `${t.x}%`, top: `${t.y}%`, width: `${t.w}%`, height: `${t.h}%`, padding: `${t.y > 0.5 ? 2 : 0}px 0 0 ${t.x > 0.5 ? 2 : 0}px`, boxSizing: 'border-box', background: '#fff' }}>
              <img src={p?.src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: focalPosition(p), display: 'block' }} />
            </span>
          );
        })}
        {g.fadeW > 0 && <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${g.fadeW}%`, background: 'linear-gradient(90deg,#fff 0%,rgba(255,255,255,.95) 48%,rgba(255,255,255,0) 100%)' }} />}
        <span style={{ position: 'absolute', left: '7%', top: '22%', width: `${g.textW * 0.8}%`, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ display: 'block', width: '34%', height: 4, borderRadius: 2, background: '#FF00FF' }} />
          <span style={{ display: 'block', width: '88%', height: 6, borderRadius: 2, background: '#000' }} />
          <span style={{ display: 'block', width: '60%', height: 6, borderRadius: 2, background: '#000' }} />
          <span style={{ display: 'block', width: '46%', height: 4, borderRadius: 2, background: '#9CA3AF' }} />
        </span>
      </span>
      <span style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '0 4px 2px' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ ...label, letterSpacing: '0.14em' }}>{meta.name}</span>
          {suggested && <span style={{ padding: '2px 7px', borderRadius: 999, background: '#00FFFF', color: '#000', fontSize: 7, fontWeight: 800, letterSpacing: '0.14em' }}>SUGGESTED</span>}
        </span>
        <span style={{ fontSize: 10, lineHeight: 1.5, opacity: 0.7 }}>{meta.hint}</span>
      </span>
    </button>
  );
}

export default function CoverReview({ eventId, form, hidden = false, onAddPhotos, mediaVersion = 0, onMediaChanged }) {
  const rootRef = useRef(null);
  const [w, setW] = useState(900);
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const measure = () => { if (el.clientWidth) setW(el.clientWidth); };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── the event as the server has it (poster, media, cover settings) ──────────────
  const [event, setEvent] = useState(null);
  const [pool, setPool] = useState([]);          // [{ id, src, fx, fy }] in cover order
  const [layout, setLayout] = useState('auto');
  const [split, setSplit] = useState(60);
  const [count, setCount] = useState(0);         // 0 = all
  const [cardId, setCardId] = useState(null);    // null = poster / first
  const [cardFocal, setCardFocal] = useState({ fx: 50, fy: 50 });
  const loadedFor = useRef(null);

  useEffect(() => {
    if (hidden || !eventId) return undefined;
    let cancelled = false;
    (async () => {
      const r = await eventService.getEvent(eventId);
      if (cancelled || !r?.success || !r.data) return;
      const ev = r.data;
      setEvent(ev);
      setPool(allCoverPhotos(ev).map(({ id, src, fx, fy }) => ({ id, src, fx, fy })));
      setLayout(ev.coverLayout || 'auto');
      setSplit(normalizeSplit(ev.coverSplit));
      setCount(Number(ev.coverPhotoCount || 0));
      setCardId(ev.cardMediaId || null);
      setCardFocal({ fx: Number.isFinite(Number(ev.cardFocalX)) ? Number(ev.cardFocalX) : 50, fy: Number.isFinite(Number(ev.cardFocalY)) ? Number(ev.cardFocalY) : 50 });
      loadedFor.current = eventId;
    })();
    return () => { cancelled = true; };
  }, [eventId, hidden, mediaVersion]);

  // ── save (debounced) ─────────────────────────────────────────────────────────────
  const pendingRef = useRef({});
  const timerRef = useRef(null);
  const [saveState, setSaveState] = useState('idle');
  const flushRef = useRef(null);
  const queueSave = useCallback((patch) => {
    if (!eventId) return;
    pendingRef.current = { ...pendingRef.current, ...patch };
    window.clearTimeout(timerRef.current);
    setSaveState('saving');
    const flush = async () => {
      timerRef.current = null;
      const payload = pendingRef.current;
      pendingRef.current = {};
      const r = await eventService.updateCover(eventId, payload);
      setSaveState(r?.success ? 'saved' : 'error');
      if (r?.success && payload.order) onMediaChanged?.();
    };
    flushRef.current = flush;
    timerRef.current = window.setTimeout(flush, SAVE_MS);
  }, [eventId, onMediaChanged]);
  // A pick followed within SAVE_MS by SAVE CHANGES / a navigation used to be dropped with the
  // timer; the pending patch is sent on the way out instead.
  useEffect(() => () => {
    if (timerRef.current == null) return;
    window.clearTimeout(timerRef.current);
    flushRef.current?.();
  }, []);

  // ── derived ──────────────────────────────────────────────────────────────────────
  const ratios = useImageRatios(pool.map((p) => p.src));
  const shaped = useMemo(() => pool.map((p) => ({ ...p, r: ratios[p.src] || 1.5, measured: !!ratios[p.src] })), [pool, ratios]);
  const empty = shaped.length === 0;
  const n = empty ? 0 : clamp(count > 0 ? count : shaped.length, 1, shaped.length);
  const cover = shaped.slice(0, n);
  const valid = validLayouts(n);
  const suggested = autoLayout(cover);
  const layoutKey = resolveLayout(layout, cover);
  const card = shaped.find((p) => p.id === cardId) || shaped.find((p) => p.id === POSTER_ID) || shaped[0] || null;
  const variant = reviewVariant(form);

  const padN = w < 600 ? 18 : 26;
  const inner = Math.max(200, w - padN * 2);
  const dZoom = inner / DESKTOP_W;
  const dH = Math.round(clamp(DESKTOP_H * dZoom, 360, 760));
  const lowerWide = w >= 820;
  const mBox = MOBILE_W * M_ZOOM;
  const cardColW = Math.max(200, lowerWide ? w - (mBox + 18 + padN * 2) - 14 - padN * 2 - 28 : inner - 28);

  const previewEvent = useMemo(() => previewEventFrom(event, form, {
    coverLayout: layoutKey, coverSplit: split, coverPhotoCount: n, cardMediaId: cardId, cardFocalX: cardFocal.fx, cardFocalY: cardFocal.fy,
  }), [event, form, layoutKey, split, n, cardId, cardFocal]);

  // ── actions ──────────────────────────────────────────────────────────────────────
  const orderOf = (list) => list.map((p) => p.id);
  const setOrder = (next) => { setPool(next); queueSave({ order: orderOf(next) }); };
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= pool.length) return;
    const next = [...pool];
    [next[i], next[j]] = [next[j], next[i]];
    setOrder(next);
  };
  const pickCount = (k) => { setCount(k); setLayout('auto'); queueSave({ photoCount: k, layout: 'auto' }); };
  const pickLayout = (k) => { setLayout(k); queueSave({ layout: k }); };
  const pickSplit = (v) => { setSplit(v); queueSave({ split: v }); };
  const reverse = () => setOrder([...pool.slice(0, n).reverse(), ...pool.slice(n)]);
  const swap = () => move(0, 1);
  const resetFraming = () => {
    const next = pool.map((p) => ({ ...p, fx: 50, fy: 50 }));
    setPool(next);
    setCardFocal({ fx: 50, fy: 50 });
    queueSave({ focal: next.map((p) => ({ id: p.id, x: 50, y: 50 })), cardFocalX: 50, cardFocalY: 50 });
  };
  const onFocal = useCallback((id, fx, fy) => {
    setPool((prev) => prev.map((p) => (p.id === id ? { ...p, fx, fy } : p)));
    pendingRef.current.focal = [...(pendingRef.current.focal || []).filter((f) => f.id !== id), { id, x: fx, y: fy }];
    queueSave({});
  }, [queueSave]);
  const pickCard = (p) => {
    setCardId(p.id === POSTER_ID ? null : p.id);
    setCardFocal({ fx: p.fx, fy: p.fy });
    queueSave({ cardId: p.id === POSTER_ID ? 'poster' : p.id, cardFocalX: p.fx, cardFocalY: p.fy });
  };
  const cardDrag = (e) => {
    if (!card) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const start = { x: e.clientX, y: e.clientY, ...cardFocal };
    const moveFn = (ev) => {
      const next = dragFocal({ r: card.r, boxW: rect.width, boxH: rect.height, startFx: start.fx, startFy: start.fy, dx: ev.clientX - start.x, dy: ev.clientY - start.y });
      setCardFocal(next);
      queueSave({ cardFocalX: next.fx, cardFocalY: next.fy });
    };
    const up = () => { window.removeEventListener('pointermove', moveFn); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', moveFn);
    window.addEventListener('pointerup', up);
  };

  const coverEdit = useMemo(() => ({ photos: cover, layout: layoutKey, split, onFocal }), [cover, layoutKey, split, onFocal]);
  const cardIsPortrait = card ? isPortrait(card) : false;
  const cardEvent = useMemo(() => ({
    ...previewEvent,
    cardImageUrl: card?.src || null,
    posterUrl: card?.src || null,
    posterAspectRatio: card?.r,
    cardFocalX: cardFocal.fx,
    cardFocalY: cardFocal.fy,
    // The wall's pledge bar counts heads against the room (MaxAttendees), not dollars.
    pledgedCount: 0,
    goalCount: previewEvent.maxAttendees || 0,
  }), [previewEvent, card, cardFocal]);

  const countTag = empty ? 'NO PHOTOS YET' : `${n} OF ${shaped.length} PHOTOS IN THE COVER`;
  const saveNote = saveState === 'saving' ? 'SAVING…' : saveState === 'saved' ? 'SAVED' : saveState === 'error' ? 'NOT SAVED — TRY AGAIN' : '';

  return (
    <div ref={rootRef} className="hev-col" hidden={hidden} data-testid="cover-review" data-variant={variant}>
      {/* ── 3a · cover setup ───────────────────────────────────────────────────── */}
      <Card gap="24" testId="cover-setup" style={{ padding: padN }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14 }}>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.16em' }}>COVER &amp; PREVIEW</span>
            <span style={{ fontSize: 12, color: '#6B7280' }}>Arrange the cover, then check it on desktop, on a phone and in the event grid.</span>
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {saveNote && <span style={{ ...small, color: saveState === 'error' ? '#DC2626' : '#9CA3AF' }} data-testid="cover-save-state">{saveNote}</span>}
            <span style={{ padding: '5px 10px', borderRadius: 999, background: '#F2F2F2', ...small }} data-testid="cover-count-tag">{countTag}</span>
          </span>
        </div>

        {empty ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '14px 18px', padding: 22, borderRadius: 20, background: '#F5F5F5' }} data-testid="cover-empty">
            <span style={{ flex: '1 1 260px', display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ ...label, fontSize: 11 }}>NO PHOTOS YET.</span>
              <span style={{ fontSize: 12, lineHeight: 1.6, color: '#6B7280' }}>The cover shows your title on white until you add one. Add photos in Place &amp; pitch to pick a layout.</span>
            </span>
            <button type="button" onClick={onAddPhotos} style={{ height: 44, padding: '0 20px', border: 'none', borderRadius: 999, background: '#000', color: '#fff', fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', cursor: 'pointer' }} data-testid="cover-add-photos">ADD PHOTOS →</button>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <span style={label}>PHOTOS</span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ ...small, color: '#6B7280' }}>IN THE COVER</span>
                  <span role="radiogroup" aria-label="Photos in the cover" style={{ display: 'flex', gap: 2, padding: 3, borderRadius: 999, background: '#F5F5F5' }}>
                    {shaped.map((_, i) => (
                      <button key={i} type="button" role="radio" aria-checked={i + 1 === n} onClick={() => pickCount(i + 1)} data-testid={`cover-count-${i + 1}`}
                        style={{ width: 32, height: 30, border: '2px solid transparent', borderRadius: 999, background: i + 1 === n ? '#000' : 'transparent', color: i + 1 === n ? '#fff' : '#000', fontFamily: 'inherit', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                        {i + 1}
                      </button>
                    ))}
                  </span>
                </span>
              </span>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(112px, 1fr))', gap: 10 }} data-testid="cover-tray">
                {shaped.map((p, i) => {
                  const inCover = i < n;
                  const isCard = card?.id === p.id;
                  return (
                    <div key={p.id} style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }} data-testid="cover-tray-tile" data-photo={p.id} data-in-cover={inCover ? '1' : '0'}>
                      <div style={{ position: 'relative', aspectRatio: '1', borderRadius: 16, overflow: 'hidden', background: '#111', opacity: inCover ? 1 : 0.4, border: `2px solid ${isCard ? '#FF00FF' : '#fff'}`, boxSizing: 'border-box' }}>
                        <img src={p.src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: focalPosition(p), display: 'block' }} />
                        <span style={{ position: 'absolute', left: 6, top: 6, minWidth: 22, height: 22, padding: '0 6px', boxSizing: 'border-box', borderRadius: 999, background: '#000', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800 }}>{i + 1}</span>
                        {isCard && <span style={{ position: 'absolute', right: 6, top: 6, padding: '4px 8px', borderRadius: 999, background: '#FF00FF', color: '#fff', fontSize: 8, fontWeight: 700, letterSpacing: '0.14em' }}>GRID CARD</span>}
                        <span style={{ position: 'absolute', left: 6, bottom: 6, display: 'flex', gap: 4 }}>
                          <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label={`Move photo ${i + 1} earlier`} style={{ width: 26, height: 26, border: 'none', borderRadius: '50%', background: '#fff', fontSize: 10, cursor: 'pointer', opacity: i === 0 ? 0.35 : 1 }}>◄</button>
                          <button type="button" onClick={() => move(i, 1)} disabled={i === shaped.length - 1} aria-label={`Move photo ${i + 1} later`} style={{ width: 26, height: 26, border: 'none', borderRadius: '50%', background: '#fff', fontSize: 10, cursor: 'pointer', opacity: i === shaped.length - 1 ? 0.35 : 1 }}>►</button>
                        </span>
                      </div>
                      <span style={{ ...small, fontSize: 9, letterSpacing: '0.12em', color: inCover ? '#000' : '#9CA3AF' }}>{inCover ? 'COVER' : 'NOT IN COVER'} · {isPortrait(p) ? 'PORTRAIT' : 'LANDSCAPE'}</span>
                      <button type="button" onClick={() => pickCard(p)} disabled={isCard} data-testid={`cover-card-pick-${p.id}`}
                        style={{ height: 28, border: 'none', borderRadius: 999, background: isCard ? '#FF00FF' : '#F2F2F2', color: isCard ? '#fff' : '#000', fontFamily: 'inherit', fontSize: 8, fontWeight: 700, letterSpacing: '0.12em', cursor: isCard ? 'default' : 'pointer' }}>
                        {isCard ? 'ON THE GRID CARD' : 'USE FOR GRID CARD'}
                      </button>
                    </div>
                  );
                })}
              </div>
              <span style={help}>Order sets which photo goes where. The GRID CARD photo is the one people see on the Events page.</span>
            </div>

            {/* ── 3b · layout picker ─────────────────────────────────────────────── */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <span style={label}>COVER LAYOUT</span>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 8 }} data-testid="cover-layouts">
                {valid.map((k) => (
                  <LayoutThumb key={k} layoutKey={k} photos={cover} split={split} on={k === layoutKey} suggested={k === suggested} onClick={() => pickLayout(k)} />
                ))}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
                {layoutKey === 'split' && (
                  <>
                    <span style={{ ...small, color: '#6B7280' }}>WIDTH</span>
                    <span role="radiogroup" aria-label="Split width" style={{ display: 'flex', gap: 2, padding: 3, borderRadius: 999, background: '#F5F5F5' }}>
                      {SPLITS.map((v) => (
                        <button key={v} type="button" role="radio" aria-checked={split === v} onClick={() => pickSplit(v)} data-testid={`cover-split-${v}`}
                          style={{ height: 32, padding: '0 12px', border: '2px solid transparent', borderRadius: 999, background: split === v ? '#000' : 'transparent', color: split === v ? '#fff' : '#000', fontFamily: 'inherit', fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', cursor: 'pointer' }}>
                          {v}/{100 - v}
                        </button>
                      ))}
                    </span>
                    <button type="button" onClick={swap} style={pillBtn(true)} data-testid="cover-swap">⇄ SWAP SIDES</button>
                  </>
                )}
                {(layoutKey === 'stack' || layoutKey === 'row') && n > 1 && (
                  <button type="button" onClick={reverse} style={pillBtn(true)} data-testid="cover-reverse">{layoutKey === 'stack' ? '⇅ REVERSE ORDER' : '⇄ REVERSE ORDER'}</button>
                )}
                <span style={{ flex: '1 1 200px', minWidth: 0, fontSize: 11, lineHeight: 1.55, color: '#6B7280' }} data-testid="cover-layout-note">{layoutNote(layoutKey, split, n)}</span>
                <button type="button" onClick={resetFraming} style={pillBtn(false)} data-testid="cover-reset-framing">RESET FRAMING</button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* ── 3c · desktop preview ─────────────────────────────────────────────────── */}
      <Card gap="12" testId="cover-preview-desktop" style={{ padding: padN }}>
        <span style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
          <span style={label}>DESKTOP</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6, ...small, color: '#6B7280' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#00FFFF' }} />
            {empty ? 'HOW THE PAGE READS WITH NO PHOTOS' : 'DRAG ANY PHOTO TO CHOOSE WHAT STAYS IN FRAME'}
          </span>
        </span>
        <div style={{ borderRadius: 18, overflow: 'hidden', background: '#000' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, height: 34, padding: '0 14px' }}>
            <span style={{ display: 'flex', gap: 5 }}>{[0, 1, 2].map((i) => <span key={i} style={{ width: 8, height: 8, borderRadius: '50%', background: '#3F3F46' }} />)}</span>
            <span style={{ flex: 1, minWidth: 0, maxWidth: 360, height: 20, padding: '0 12px', borderRadius: 999, background: '#1A1A1A', color: '#9CA3AF', display: 'flex', alignItems: 'center', fontSize: 9, letterSpacing: '0.08em', whiteSpace: 'nowrap', overflow: 'hidden' }}>heyportal.app/events/{event?.inviteCode || '…'}</span>
          </div>
          <div style={{ height: dH, overflowY: 'auto', overflowX: 'hidden', background: '#F5F5F5' }} data-testid="cover-preview-desktop-scroll">
            <div style={{ width: DESKTOP_W, zoom: dZoom }}>
              <PreviewChrome />
              <EventApplyPage previewEvent={previewEvent} previewFrame="desktop" coverEdit={coverEdit} embedded />
            </div>
          </div>
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: lowerWide ? `${mBox + 18 + padN * 2}px minmax(0,1fr)` : 'minmax(0,1fr)', gap: 14, alignItems: 'start' }}>
        {/* ── 3d · mobile preview ────────────────────────────────────────────────── */}
        <Card gap="12" testId="cover-preview-mobile" style={{ padding: padN, alignItems: 'center' }}>
          <span style={{ alignSelf: 'stretch', ...label }}>MOBILE</span>
          <div style={{ padding: 9, borderRadius: 40, background: '#000' }}>
            <div style={{ width: mBox, height: Math.round(MOBILE_H * M_ZOOM), borderRadius: 31, overflow: 'hidden', background: '#F5F5F5' }}>
              <div style={{ height: '100%', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'none' }} data-testid="cover-preview-mobile-scroll">
                <div style={{ width: MOBILE_W, zoom: M_ZOOM, minHeight: MOBILE_H, display: 'flex', flexDirection: 'column' }}>
                  <EventApplyPage previewEvent={previewEvent} previewFrame="mobile" coverEdit={coverEdit} embedded />
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* ── 3e · grid card preview ────────────────────────────────────────────── */}
        <Card gap="14" testId="cover-preview-card" style={{ padding: padN, minWidth: 0 }}>
          <span style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={label}>EVENT GRID CARD</span>
            <span style={help}>This is how the event shows on the Events page. Drag the photo to reframe it for the card only.</span>
          </span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 14px 6px 6px', borderRadius: 999, background: '#F5F5F5' }} data-testid="cover-card-line">
            {card ? <img src={card.src} alt="" style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', objectPosition: `${cardFocal.fx}% ${cardFocal.fy}%`, display: 'block' }} /> : <span style={{ width: 34, height: 34, borderRadius: '50%', background: '#0F0F0F', display: 'block' }} />}
            <span style={{ flex: 1, minWidth: 0, ...label, fontSize: 10, letterSpacing: '0.12em' }}>{card ? `PHOTO ${shaped.indexOf(card) + 1} SHOWS IN THE EVENT GRID` : 'NO PHOTO · THE CARD USES A PLACEHOLDER'}</span>
            <span style={{ padding: '4px 8px', borderRadius: 999, background: '#FF00FF', color: '#fff', fontSize: 8, fontWeight: 700, letterSpacing: '0.14em' }}>GRID CARD</span>
          </span>
          {[['3-COLUMN GRID · NARROW CARD', 380], ['2-COLUMN GRID · WIDE CARD', 600]].map(([name, cw]) => {
            const zoom = clamp(cardColW / cw, 0.3, 1);
            return (
              <div key={cw} style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }} data-testid={`cover-card-${cw}`}>
                <span style={{ ...small, color: '#6B7280' }}>{name}</span>
                <div style={{ background: '#F5F5F5', padding: 14, borderRadius: 18, overflow: 'hidden' }}>
                  <div style={{ width: cw, zoom, pointerEvents: 'none' }}>
                    <WallEventCard
                      event={cardEvent}
                      width={cw}
                      ratio={card ? cardGeometry(card.r, cw).aspect && card.r : undefined}
                      onOpen={() => {}}
                      imageProps={{ onPointerDown: card ? cardDrag : undefined, style: { cursor: card ? 'grab' : 'default', touchAction: 'none', pointerEvents: 'auto' }, 'data-testid': 'cover-card-image' }}
                      imagePosition={`${cardFocal.fx}% ${cardFocal.fy}%`}
                    />
                  </div>
                </div>
              </div>
            );
          })}
          {cardIsPortrait && <span style={help}>Portrait photos stand beside the text on wide cards and sit on top on narrow ones.</span>}
        </Card>
      </div>
    </div>
  );
}

/** A static stand-in for the site header inside the desktop preview (the real bar is sticky chrome). */
function PreviewChrome() {
  const nav = ['EVENTS', 'PEOPLE', 'CLUBS', 'CALENDAR'];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20, height: 72, padding: '0 32px', background: '#fff', borderBottom: '2px solid #000', boxSizing: 'border-box' }} aria-hidden="true">
      <span style={{ fontSize: 22, fontWeight: 800, letterSpacing: '0.18em' }}>PORTAL</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: 28, fontSize: 12, fontWeight: 600, letterSpacing: '0.16em' }}>{nav.map((n) => <span key={n}>{n}</span>)}</span>
      <span style={{ flex: 1 }} />
      <span style={{ width: 320, height: 44, border: '2px solid #000', boxSizing: 'border-box', display: 'flex', alignItems: 'center', padding: '0 14px', fontSize: 12, letterSpacing: '0.1em', color: '#6B7280' }}>SEARCH EVENTS, PEOPLE, CLUBS</span>
      <span style={{ width: 44, height: 44, border: '2px solid #000', background: '#F2F2F2', boxSizing: 'border-box' }} />
    </div>
  );
}
